import { useEffect, useState } from "react";
import { formatUzPhone } from "../phone";
import {
  api,
  daysUntil,
  fmtDate,
  fmtDateTime,
  STATUS_LABEL,
  TARIFF_LABEL,
  TARIFF_ORDER,
  type Tariff,
  type WorkshopDetail as WD,
  type WorkshopStatus,
} from "../api";
import { HealthBadge, StatusBadge, TariffBadge } from "../components/Badge";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { BarTrend } from "../components/BarTrend";
import { bucketByWeek } from "../util/buckets";
import type { Location } from "../routing";

export function WorkshopDetail({
  id,
  onNavigate,
}: {
  id: number;
  onNavigate: (loc: Location) => void;
}) {
  const [ws, setWs] = useState<WD | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    setWs(null); setErr(null);
    api.workshopDetail(id).then(setWs).catch((e) => setErr((e as Error).message));
  }, [id, refresh]);

  if (err) return (
    <>
      <button className="back-link" onClick={() => onNavigate({ route: "workshops" })}>← Ustaxonalar</button>
      <div className="error">{err}</div>
    </>
  );
  if (!ws) return (
    <>
      <button className="back-link" onClick={() => onNavigate({ route: "workshops" })}>← Ustaxonalar</button>
      <div className="skel" style={{ height: 28, width: 240, marginBottom: 16 }} />
      <div className="panel"><div className="skel" style={{ height: 120 }} /></div>
    </>
  );

  const trialDays = daysUntil(ws.trial_end);

  return (
    <>
      <button className="back-link" onClick={() => onNavigate({ route: "workshops" })}>← Ustaxonalar</button>

      {ws.status === "archived" && (
        <div className="error" style={{ fontWeight: 600 }}>
          ⚠️ Bu ustaxona arxivlangan — barcha kirish bloklangan
        </div>
      )}

      <div className="page-head">
        <div>
          <h1 style={{ marginBottom: 8 }}>{ws.name}</h1>
          <div className="row" style={{ marginTop: 4 }}>
            <TariffBadge tariff={ws.tariff} />
            <StatusBadge ws={ws} />
            <HealthBadge health={ws.health} reason={ws.reason} />
            {ws.tariff === "trial" && trialDays !== null && (
              <span className="muted">
                {trialDays > 0 ? `${trialDays} kun qoldi` : "Trial tugagan"}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Salomatlik banneri — bir qarashda diagnostika */}
      <div className="panel" style={{ marginBottom: 16 }}>
        <div className="row" style={{ gap: 28, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div>
            <div className="label" style={{ fontSize: 11, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600 }}>Oxirgi ish</div>
            <div style={{ fontSize: 24, fontWeight: 800 }}>
              {ws.last_work_at ? fmtDate(ws.last_work_at) : "Hech qachon"}
            </div>
            <div className="muted" style={{ fontSize: 12.5 }}>
              {ws.days_since_last_work === null ? "Hali ish yo'q" : `${ws.days_since_last_work} kun oldin`}
            </div>
          </div>
          <div>
            <div className="label" style={{ fontSize: 11, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600 }}>Salomatlik</div>
            <div style={{ marginTop: 4 }}><HealthBadge health={ws.health} reason={ws.reason} /></div>
            <div className="muted" style={{ fontSize: 12.5, marginTop: 2 }}>{ws.reason}</div>
          </div>
          {ws.tariff === "trial" && trialDays !== null && (
            <div>
              <div className="label" style={{ fontSize: 11, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600 }}>Trial</div>
              <div style={{ fontSize: 24, fontWeight: 800 }}>{trialDays > 0 ? `${trialDays} kun` : "tugagan"}</div>
              <div className="muted" style={{ fontSize: 12.5 }}>{fmtDate(ws.trial_end)}</div>
            </div>
          )}
        </div>
      </div>

      <div className="detail-grid">
        <div>
          <div className="panel">
            <h2 style={{ marginTop: 0 }}>Ma'lumotlar</h2>
            <div className="kv">
              <div className="k">ID</div><div>#{ws.id}</div>
              <div className="k">Egasi</div><div>{ws.owner_name ?? "—"}</div>
              <div className="k">Telefon</div><div>{formatUzPhone(ws.owner_phone) || "—"}</div>
              <div className="k">Yaratilgan</div><div>{fmtDateTime(ws.created_at)}</div>
              <div className="k">Trial tugashi</div><div>{fmtDate(ws.trial_end)}</div>
              <div className="k">Tarif</div><div>{TARIFF_LABEL[ws.tariff]}</div>
              <div className="k">Holat</div><div>{STATUS_LABEL[ws.status as WorkshopStatus] ?? ws.status}</div>
              {ws.archived_at && (<>
                <div className="k">Arxivlangan</div><div>{fmtDateTime(ws.archived_at)}</div>
              </>)}
            </div>
          </div>

          <div className="panel">
            <h2 style={{ marginTop: 0 }}>Xodimlar faolligi</h2>
            {ws.staff.length === 0 ? (
              <div className="empty">Xodim yo'q</div>
            ) : (
              <table className="table">
                <thead><tr><th>Xodim</th><th className="num">Ishlar</th><th>Oxirgi ish</th></tr></thead>
                <tbody>
                  {ws.staff.map((s) => (
                    <tr key={s.user_id}>
                      <td><strong>{s.full_name}</strong></td>
                      <td className="num">{s.work_count}</td>
                      <td className={s.last_work_at ? "" : "muted"}>
                        {s.last_work_at ? fmtDate(s.last_work_at) : "Hech qachon"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="panel">
            <h2 style={{ marginTop: 0 }}>Filiallar</h2>
            {ws.branches.length === 0 ? (
              <div className="empty">Filial yo'q</div>
            ) : (
              <table className="table">
                <thead><tr><th>ID</th><th>Nomi</th></tr></thead>
                <tbody>
                  {ws.branches.map((b) => (
                    <tr key={b.id}><td className="muted">#{b.id}</td><td>{b.name}</td></tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div>
          <div className="detail-stats">
            <div className="kpi"><div className="label">Mijoz</div><div className="value">{ws.clients_count}</div></div>
            <div className="kpi"><div className="label">Ish</div><div className="value">{ws.works_count}</div></div>
            <div className="kpi"><div className="label">Filial</div><div className="value">{ws.branches.length}</div></div>
            <div className="kpi"><div className="label">Xodim</div><div className="value">{ws.employees_count}</div></div>
          </div>

          <WorkTrend points={ws.works_by_day} />
          <LinkRatePanel linked={ws.linked_client_count} total={ws.clients_count} rate={ws.link_rate} />
          <ManualMessagingPanel ws={ws} onSaved={() => setRefresh((x) => x + 1)} />

          <TariffForm ws={ws} onSaved={() => setRefresh((x) => x + 1)} />
          <PasswordResetForm wsId={ws.id} />
          <DangerZone ws={ws} onArchived={() => onNavigate({ route: "workshops" })} />
        </div>
      </div>
    </>
  );
}

function WorkTrend({ points }: { points: WD["works_by_day"] }) {
  const weeks = bucketByWeek(points);
  const total = points.reduce((s, p) => s + p.count, 0);
  const cur = weeks[weeks.length - 1]?.count ?? 0;
  const prev = weeks[weeks.length - 2]?.count ?? 0;
  const dir = cur > prev ? "↑" : cur < prev ? "↓" : "→";
  const dirClass = cur > prev ? "up" : cur < prev ? "down" : "";

  const summary = weeks.length >= 2 && (
    <>
      Bu hafta: <strong>{cur}</strong> · O'tgan hafta: <strong>{prev}</strong>{" "}
      <span className={dirClass}>{dir}</span>
    </>
  );

  return (
    <div className="panel">
      <div className="panel-title">
        <h2 style={{ margin: 0 }}>So'nggi 30 kun ishlari</h2>
        <span className="muted">{total} ta</span>
      </div>
      <BarTrend buckets={weeks} emptyText="So'nggi 30 kunda ish yo'q" summary={summary} />
    </div>
  );
}

function LinkRatePanel({ linked, total, rate }: { linked: number; total: number; rate: number | null }) {
  const pct = rate === null ? null : Math.round(rate * 100);
  return (
    <div className="panel">
      <div className="panel-title"><h2 style={{ margin: 0 }}>Telegram ulanish darajasi</h2></div>
      <div className="row" style={{ alignItems: "baseline", gap: 10 }}>
        <div style={{ fontSize: 24, fontWeight: 800 }}>{pct === null ? "—" : `${pct}%`}</div>
        <div className="muted" style={{ fontSize: 13 }}>{linked} / {total} mijoz ulangan</div>
      </div>
      <div className="dist-bar" style={{ marginTop: 10 }}>
        {linked > 0 && <div className="seg pro" style={{ flex: linked }} />}
        {total - linked > 0 && <div className="seg" style={{ flex: total - linked, background: "var(--border-strong)" }} />}
      </div>
    </div>
  );
}

function ManualMessagingPanel({ ws, onSaved }: { ws: WD; onSaved: () => void }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function toggle() {
    setErr(null);
    setBusy(true);
    try {
      await api.setManualMessaging(ws.id, !ws.manual_messaging_enabled);
      onSaved();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="panel">
      <div className="panel-title">
        <h2 style={{ margin: 0 }}>Qo'lda xabar yuborish</h2>
        <span className={ws.manual_messaging_enabled ? "" : "muted"}>
          {ws.manual_messaging_enabled ? "Yoqilgan" : "O'chirilgan"}
        </span>
      </div>
      {err && <div className="error">{err}</div>}
      <div className="row" style={{ gap: 28, alignItems: "flex-end", marginTop: 4 }}>
        <div style={{ minWidth: 90 }}>
          <div className="label" style={{ fontSize: 11, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600 }}>Transactional 24 soat</div>
          <div style={{ fontSize: 24, fontWeight: 800 }}>{ws.manual_msgs_24h}</div>
        </div>
        <div style={{ minWidth: 90 }}>
          <div className="label" style={{ fontSize: 11, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600 }}>Transactional 7 kun</div>
          <div style={{ fontSize: 24, fontWeight: 800 }}>{ws.manual_msgs_7d}</div>
        </div>
        <div style={{ minWidth: 90 }}>
          <div className="label" style={{ fontSize: 11, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600 }}>Broadcast 24 soat</div>
          <div style={{ fontSize: 24, fontWeight: 800 }}>{ws.broadcast_msgs_24h}</div>
        </div>
        <div style={{ minWidth: 90 }}>
          <div className="label" style={{ fontSize: 11, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600 }}>Broadcast 7 kun</div>
          <div style={{ fontSize: 24, fontWeight: 800 }}>{ws.broadcast_msgs_7d}</div>
        </div>
      </div>
      <div className="row" style={{ justifyContent: "flex-end", marginTop: 12 }}>
        <button
          className={"btn ghost" + (ws.manual_messaging_enabled ? " danger" : "")}
          onClick={toggle}
          disabled={busy}
        >
          {busy
            ? "..."
            : ws.manual_messaging_enabled
            ? "Xabar yuborishni o'chirish"
            : "Xabar yuborishni yoqish"}
        </button>
      </div>
    </div>
  );
}

function TariffForm({ ws, onSaved }: { ws: WD; onSaved: () => void }) {
  const [tariff, setTariff] = useState<Tariff>(ws.tariff);
  const [status, setStatus] = useState<WorkshopStatus>(ws.status);
  const [extendDays, setExtendDays] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState<null | { status: WorkshopStatus }>(null);

  async function doSave() {
    setErr(null); setOkMsg(null); setBusy(true);
    try {
      const data: Record<string, unknown> = {};
      if (tariff !== ws.tariff) data.tariff = tariff;
      if (status !== ws.status) data.status = status;
      if (extendDays && parseInt(extendDays, 10) > 0) data.extend_days = parseInt(extendDays, 10);
      if (Object.keys(data).length === 0) {
        setErr("Hech narsa o'zgarmagan");
        setBusy(false);
        return;
      }
      await api.updateWorkshop(ws.id, data);
      setExtendDays("");
      setOkMsg("Saqlandi");
      onSaved();
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (status !== ws.status && (status === "suspended" || status === "expired")) {
      setConfirm({ status });
      return;
    }
    doSave();
  }

  return (
    <>
      <form className="panel" onSubmit={submit}>
        <h2 style={{ marginTop: 0 }}>Tarif va holat</h2>
        {err && <div className="error">{err}</div>}
        {okMsg && <div className="note">{okMsg}</div>}
        <div className="form">
          <div className="grid-2">
            <div className="field">
              <label>Tarif</label>
              <select value={tariff} onChange={(e) => setTariff(e.target.value as Tariff)}>
                {TARIFF_ORDER.map((t) => (
                  <option key={t} value={t}>{TARIFF_LABEL[t]}</option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>Holat</label>
              <select value={status} onChange={(e) => setStatus(e.target.value as WorkshopStatus)}>
                <option value="active">Faol</option>
                <option value="suspended">To'xtatilgan</option>
                <option value="expired">Muddati tugagan</option>
              </select>
            </div>
          </div>
          <div className="field">
            <label>Muddatni uzaytirish (kun)</label>
            <input type="number" min="0" max="730" placeholder="0" value={extendDays} onChange={(e) => setExtendDays(e.target.value)} />
            <div className="help">trial_end sanasiga shuncha kun qo'shiladi.</div>
          </div>
          <div className="row" style={{ justifyContent: "flex-end" }}>
            <button type="submit" className="btn" disabled={busy}>{busy ? "..." : "Saqlash"}</button>
          </div>
        </div>
      </form>

      {confirm && (
        <ConfirmDialog
          title="Holatni o'zgartirish"
          body={<>Ustaxona holatini <strong>{STATUS_LABEL[confirm.status]}</strong> ga o'tkazasizmi? Bu egasi va xodimlar ishlashiga ta'sir qiladi.</>}
          confirmLabel="Ha, o'zgartirish"
          danger
          onCancel={() => setConfirm(null)}
          onConfirm={async () => { setConfirm(null); await doSave(); }}
        />
      )}
    </>
  );
}

function PasswordResetForm({ wsId }: { wsId: number }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!window.confirm("Egasining parolini 12345678 ga tiklaysizmi?")) return;
    setErr(null); setBusy(true);
    try {
      await api.resetOwnerPassword(wsId);
      setDone(true);
      setTimeout(() => setDone(false), 4000);
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  }

  return (
    <form className="panel" onSubmit={submit}>
      <h2 style={{ marginTop: 0 }}>Egasining parolini reset qilish</h2>
      {err && <div className="error">{err}</div>}
      {done && <div className="note">Parol 12345678 ga tiklandi. Egasiga xabar bering.</div>}
      <div className="form">
        <div className="help">Bu amal egasining parolini <strong>12345678</strong> ga tiklaydi.</div>
        <div className="row" style={{ justifyContent: "flex-end" }}>
          <button type="submit" className="btn ghost" disabled={busy}>
            {busy ? "..." : "Parolni 12345678 ga tiklash"}
          </button>
        </div>
      </div>
    </form>
  );
}

function DangerZone({ ws, onArchived }: { ws: WD; onArchived: () => void }) {
  const [confirm, setConfirm] = useState(false);
  if (ws.status === "archived") return null;

  return (
    <>
      <div className="danger-zone">
        <h3>Xavfli zona</h3>
        <p>Ustaxonani arxivlash uni ro'yxatdan yashiradi. Ma'lumotlar saqlanib qoladi, ammo ustaxona ishlay olmaydi.</p>
        <button className="btn danger ghost" onClick={() => setConfirm(true)}>Ustaxonani arxivlash</button>
      </div>
      {confirm && (
        <ConfirmDialog
          title="Ustaxonani arxivlash"
          body={<><strong>{ws.name}</strong> ustaxonasi arxivlanadi. Egasi va xodimlar tizimga kira olmaydi.</>}
          confirmLabel="Ha, arxivlash"
          danger
          onCancel={() => setConfirm(false)}
          onConfirm={async () => {
            await api.archiveWorkshop(ws.id);
            onArchived();
          }}
        />
      )}
    </>
  );
}
